import { useCallback } from 'react';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import {
  clearDependentFilterValues,
  countActiveFilters,
  fillEmptyFilterValues,
} from '@/utils/filters';

type FilterValues = Record<string, string>;

const NO_DEFAULT_VALUES: Partial<FilterValues> = {};
const NO_RANGE_KEYS: readonly string[] = [];

export type UrlSyncedFilters = ReturnType<typeof useUrlSyncedFilters>;

interface UseUrlSyncedFiltersOptions {
  appliedValues: FilterValues;
  onApply: (values: FilterValues) => void;
  normalizeValues?: ((values: FilterValues) => FilterValues) | undefined;
  defaultValues?: Partial<FilterValues> | undefined;
  rangeKeys?: readonly string[] | undefined;
  shouldWaitForApply?: boolean | undefined;
}

export function useUrlSyncedFilters({
  appliedValues,
  onApply,
  normalizeValues,
  defaultValues = NO_DEFAULT_VALUES,
  rangeKeys = NO_RANGE_KEYS,
  shouldWaitForApply = false,
}: UseUrlSyncedFiltersOptions) {
  const { draftValues, setDraftValues, applyValues, hasPendingChanges } = useFilterDraft({
    appliedValues,
    onApply,
    normalizeValues,
    shouldWaitForApply,
  });

  const toNextValues = useCallback(
    (previousValues: FilterValues, nextValues: FilterValues) =>
      fillEmptyFilterValues(
        clearDependentFilterValues(previousValues, nextValues),
        defaultValues,
        rangeKeys,
      ),
    [defaultValues, rangeKeys],
  );

  const toNextDraftValues = useCallback(
    (previousValues: FilterValues, nextValues: FilterValues) =>
      shouldWaitForApply
        ? clearDependentFilterValues(previousValues, nextValues)
        : toNextValues(previousValues, nextValues),
    [shouldWaitForApply, toNextValues],
  );

  const updateFilterValues = useCallback(
    (update: (previousValues: FilterValues) => FilterValues) => {
      setDraftValues((previousValues) => toNextDraftValues(previousValues, update(previousValues)));
    },
    [setDraftValues, toNextDraftValues],
  );

  const handleFilterChange = useCallback(
    (nextValues: FilterValues) => updateFilterValues(() => nextValues),
    [updateFilterValues],
  );

  function applyFilterValues(nextValues: FilterValues) {
    applyValues(toNextValues(draftValues, nextValues));
  }

  return {
    filterValues: draftValues,
    defaultFilterValues: defaultValues,
    requiredFilterKeys: rangeKeys,
    activeFilterCount: countActiveFilters(shouldWaitForApply ? appliedValues : draftValues),
    hasPendingChanges,
    handleFilterChange,
    updateFilterValues,
    applyFilterValues: shouldWaitForApply ? applyFilterValues : undefined,
  };
}

export function getFilterPanelProps(filters: UrlSyncedFilters) {
  return {
    values: filters.filterValues,
    defaultValues: filters.defaultFilterValues,
    requiredKeys: filters.requiredFilterKeys,
    onChange: filters.handleFilterChange,
    onApply: filters.applyFilterValues,
    hasPendingChanges: filters.hasPendingChanges,
  };
}
